import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { TownSocket, api, clearToken } from "../api";
import { registerWebmcpTools } from "../webmcp";
import { TownRenderer } from "../game/renderer";
import { pickTopmostAgent } from "../game/selection";
import type { AgentSnapshot, WorldState, Post, AgentProfile, ChatMsg, Building, BoardMessage, BoardQuota } from "../../shared/types";
import { WEATHERS } from "../../shared/types";

export default function Town() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const rendererRef = useRef<TownRenderer | null>(null);
  const sockRef = useRef<TownSocket | null>(null);
  const stateRef = useRef<any>({ agents: [], tiles: [], buildings: [], homeSpots: [], gameMin: 480, weather: "sunny", myAgentId: null, selectedId: null });

  const [ready, setReady] = useState(false);
  const [clock, setClock] = useState({ day: 1, gameMin: 480, weather: "sunny" });
  const [llmActive, setLlmActive] = useState(false);
  const [posts, setPosts] = useState<Post[]>([]);
  const [showFeed, setShowFeed] = useState(false);
  const [selected, setSelected] = useState<AgentProfile | null>(null);
  const [myAgentId, setMyAgentId] = useState<string | null>(null);
  const [showMine, setShowMine] = useState(false);
  const [usage, setUsage] = useState<any>(null);
  const [showBoard, setShowBoard] = useState(false);
  const [boardMsgs, setBoardMsgs] = useState<BoardMessage[]>([]);
  const [boardQuota, setBoardQuota] = useState<BoardQuota | null>(null);
  const [boardInput, setBoardInput] = useState("");
  const [boardSending, setBoardSending] = useState(false);
  const nav = useNavigate();

  // 初始化
  useEffect(() => {
    registerWebmcpTools(api);
    const canvas = canvasRef.current!;
    const renderer = new TownRenderer(canvas);
    rendererRef.current = renderer;
    renderer.resize();
    window.addEventListener("resize", () => renderer.resize());

    const sock = new TownSocket();
    sockRef.current = sock;
    sock.on((msg) => {
      if (msg.type === "init") {
        stateRef.current = {
          ...stateRef.current,
          tiles: msg.world.tiles,
          width: msg.world.width,
          height: msg.world.height,
          buildings: msg.buildings,
          homeSpots: msg.homeSpots,
          agents: msg.agents,
          gameMin: msg.world.gameMin,
          weather: msg.world.weather,
          myAgentId: msg.myAgentId,
        };
        setMyAgentId(msg.myAgentId);
        setLlmActive(msg.world.llmActive);
        setClock({ day: msg.world.day, gameMin: msg.world.gameMin, weather: msg.world.weather });
        setPosts(msg.posts);
        setReady(true);
      } else if (msg.type === "tick") {
        // 增量广播:只更新变化的角色,静止角色保持原位(数组顺序稳定,保证渲染层级)
        const cur = stateRef.current.agents;
        if (msg.full || !Array.isArray(cur) || cur.length === 0) {
          stateRef.current.agents = msg.agents;
        } else if (msg.agents && msg.agents.length) {
          const map = new Map(cur.map((a: any) => [a.id, a]));
          for (const a of msg.agents) map.set(a.id, a);
          stateRef.current.agents = [...map.values()];
        }
        if (Array.isArray(msg.left) && msg.left.length) {
          const gone = new Set(msg.left);
          stateRef.current.agents = stateRef.current.agents.filter((a: any) => !gone.has(a.id));
        }
        stateRef.current.gameMin = msg.gameMin;
        stateRef.current.weather = msg.weather;
        setClock((c) => (c.gameMin !== msg.gameMin || c.day !== msg.day || c.weather !== msg.weather
          ? { day: msg.day, gameMin: msg.gameMin, weather: msg.weather } : c));
      } else if (msg.type === "post") {
        setPosts((p) => [msg.post, ...p].slice(0, 60));
      } else if (msg.type === "error") {
        clearToken();
        nav("/auth");
      }
    });
    sock.connect();

    renderer.onTileClick = (x, y) => {
      const agents: AgentSnapshot[] = stateRef.current.agents;
      // 命中候选按"最上层优先":渲染时 y 大者后绘制、在最上层;同格时数组靠后者也在最上层
      const hit = pickTopmostAgent(agents, x, y, 0.8);
      if (hit) {
        selectAgent(hit.id);
      } else if (stateRef.current.myAgentId) {
        sock.send({ type: "move", x: Math.round(x), y: Math.round(y) });
        renderer.followMyAgent = true;
      }
    };

    let raf = 0;
    const loop = (t: number) => {
      const s = stateRef.current;
      if (s.tiles.length) {
        renderer.render({
          tiles: s.tiles, width: s.width, height: s.height,
          agents: s.agents, buildings: s.buildings, homeSpots: s.homeSpots,
          gameMin: s.gameMin, weather: s.weather,
          myAgentId: s.myAgentId, selectedId: s.selectedId,
        }, t);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => { cancelAnimationFrame(raf); sock.close(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectAgent = useCallback(async (id: string) => {
    stateRef.current.selectedId = id;
    setShowMine(false);
    try {
      const res = await api.profile(id);
      setSelected(res.profile);
    } catch { /* ignore */ }
  }, []);

  // LLM 用量(每 60s 刷新)
  useEffect(() => {
    let alive = true;
    const load = () => api.usage(24).then((r) => alive && setUsage(r)).catch(() => {});
    load();
    const t = setInterval(load, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const closePanel = () => {
    setSelected(null);
    setShowMine(false);
    stateRef.current.selectedId = null;
  };

  const toggleBoard = async () => {
    setShowBoard((v) => !v);
    if (!showBoard) {
      try {
        const r = await api.board();
        setBoardMsgs(r.messages);
        setBoardQuota(r.quota);
      } catch { /* ignore */ }
    }
  };

  const postBoard = async () => {
    const text = boardInput.trim();
    if (!text || boardSending) return;
    setBoardInput("");
    setBoardSending(true);
    try {
      const r = await api.postBoard(text);
      setBoardMsgs((m) => [r.message, ...m]);
      setBoardQuota((q) => (q ? { ...q, used: q.used + 1 } : q));
    } catch (e: any) {
      setBoardInput(text);
      alert(e.message || "留言失败");
    } finally {
      setBoardSending(false);
    }
  };

  const h = Math.floor(clock.gameMin / 60), m = Math.floor(clock.gameMin % 60);
  const timeStr = `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  const w = WEATHERS[clock.weather] || WEATHERS.sunny;

  return (
    <div className="h-full w-full relative overflow-hidden bg-[#1a2340]">
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full cursor-pointer" />

      {!ready && (
        <div className="absolute inset-0 flex items-center justify-center text-[#F8F7E5] z-20 bg-[#10173a]">
          <div className="text-center">
            <div className="text-4xl mb-3 animate-twinkle inline-block">⭐</div>
            <div>小镇正在苏醒……</div>
          </div>
        </div>
      )}

      {/* 顶栏 */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center gap-2 px-3 py-2 pointer-events-none">
        <div className="pointer-events-auto flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-bold text-[#1D1D1D]"
          style={{ background: "rgba(248,247,229,0.95)", boxShadow: "0 2px 10px rgba(0,0,0,0.3)" }}>
          <span>📅 第{clock.day}天</span>
          <span className="opacity-40">|</span>
          <span className="tabular-nums">{timeStr}</span>
          <span className="opacity-40">|</span>
          <span>{w.emoji} {w.name}</span>
        </div>
        <div className="pointer-events-auto rounded-full px-3 py-1.5 text-xs font-bold"
          style={{ background: llmActive ? "rgba(134,239,172,0.9)" : "rgba(253,224,71,0.9)", color: "#1D1D1D" }}
          title={llmActive ? "大模型已接入,居民由 AI 驱动" : "未配置 LLM,使用内置行为引擎(配置 LLM_API_KEY 后获得完整体验)"}>
          {llmActive ? "🧠 AI 驱动中" : "⚙️ 内置引擎"}
        </div>
        {usage && (
          <div className="pointer-events-auto rounded-full px-3 py-1.5 text-xs font-bold text-[#F8F7E5]"
            style={{ background: "rgba(16,23,58,0.75)", border: "1px solid rgba(248,247,229,0.25)" }}
            title={`24h LLM 调用 ${usage.window?.n ?? 0} 次(输入 ${usage.window?.prompt ?? 0} / 输出 ${usage.window?.completion ?? 0} tokens),失败 ${(usage.window?.n ?? 0) - (usage.window?.ok ?? 0)} 次`}>
            🪙 {(usage.window?.total ?? 0).toLocaleString()} tokens/24h
          </div>
        )}
        <div className="flex-1" />
        <TopBtn onClick={() => { const r = rendererRef.current; if (r) r.followMyAgent = true; }}>🎯 视角</TopBtn>
        <TopBtn onClick={() => { setShowFeed((v) => !v); }}>📰 广场</TopBtn>
        <TopBtn onClick={toggleBoard}>📮 留言板</TopBtn>
        <TopBtn onClick={() => { setShowMine(true); setSelected(null); stateRef.current.selectedId = null; }}>⭐ 我的角色</TopBtn>
        <TopBtn onClick={() => { clearToken(); nav("/"); }}>退出</TopBtn>
      </div>

      {/* 底部提示 */}
      <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-10 rounded-full px-4 py-1 text-xs text-[#F8F7E5] pointer-events-none"
        style={{ background: "rgba(16,23,58,0.7)" }}>
        点击地面移动 · 点击居民查看/聊天 · 拖动平移 · 滚轮缩放
      </div>

      {/* 广场信息流 */}
      {showFeed && (
        <div className="absolute top-14 left-3 bottom-12 w-80 max-w-[85vw] z-10 rounded-2xl overflow-hidden flex flex-col"
          style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
          <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
            style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
            📰 小镇广场
            <button onClick={() => setShowFeed(false)} className="text-[#8a8571] text-sm">✕</button>
          </div>
          <div className="flex-1 overflow-y-auto p-3 space-y-2.5">
            {posts.map((p) => (
              <div key={p.id} className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-3 h-3 rounded-full inline-block" style={{ background: p.agentColor }} />
                  <span className="text-xs font-bold text-[#1D1D1D]">{p.agentName}</span>
                  <span className="text-[10px] text-[#a9a494] ml-auto">第{p.day}天 {fmtMin(p.gameMin)}</span>
                </div>
                <div className="text-sm text-[#4a453a] leading-relaxed">{p.content}</div>
              </div>
            ))}
            {posts.length === 0 && <div className="text-center text-[#a9a494] text-sm py-8">还没有动态,小镇刚刚醒来</div>}
          </div>
        </div>
      )}

      {/* 角色面板 */}
      {selected && <AgentPanel profile={selected} onClose={closePanel} onRefresh={() => selectAgent(selected.id)} />}

      {/* 我的角色面板 */}
      {showMine && myAgentId && <MyAgentPanel agentId={myAgentId} onClose={closePanel} />}

      {/* 小镇留言板 */}
      {showBoard && (
        <div className="absolute top-14 right-3 bottom-12 w-80 max-w-[85vw] z-10 rounded-2xl overflow-hidden flex flex-col"
          style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
          <div className="px-4 py-3 font-black text-[#1D1D1D] border-b border-[#ddd5b8] flex items-center justify-between"
            style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
            📮 小镇留言板
            <button onClick={() => setShowBoard(false)} className="text-[#8a8571] text-sm">✕</button>
          </div>
          {boardQuota?.isGuest && (
            <div className="px-4 py-1.5 text-[11px] text-[#b45309] bg-[#fef3e2] border-b border-[#f0dfc0]">
              游客身份 24 小时内还可留言 {Math.max(0, boardQuota.limit - boardQuota.used)} / {boardQuota.limit} 条
            </div>
          )}
          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {boardMsgs.map((m) => (
              <div key={m.id} className="rounded-xl p-3 bg-white border border-[#eee8d0]">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-bold text-[#1D1D1D]">
                    {m.name}
                    {m.isGuest ? <span className="text-[10px] text-[#b45309]"> 🚀游客</span> : null}
                  </span>
                  <span className="text-[10px] text-[#a9a494] ml-auto">{fmtTs(m.ts)}</span>
                </div>
                <div className="text-sm text-[#4a453a] leading-relaxed break-words">{m.content}</div>
              </div>
            ))}
            {boardMsgs.length === 0 && <div className="text-center text-[#a9a494] text-sm py-8">还没有留言,来写下第一句话吧</div>}
          </div>
          <div className="p-3 border-t border-[#ddd5b8] flex gap-2">
            <input value={boardInput} onChange={(e) => setBoardInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && postBoard()}
              placeholder="在小镇留言板上写点什么…" maxLength={100}
              className="flex-1 px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#f59e0b] text-sm" />
            <button onClick={postBoard} disabled={boardSending || !boardInput.trim()}
              className="px-4 py-2 rounded-xl font-bold text-sm text-[#1D1D1D] disabled:opacity-50"
              style={{ background: "#e8c96a" }}>
              留言
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function fmtMin(min: number) {
  return `${String(Math.floor(min / 60)).padStart(2, "0")}:${String(Math.floor(min % 60)).padStart(2, "0")}`;
}

function fmtTs(ts: number) {
  const d = new Date(ts);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function TopBtn({ children, onClick }: { children: React.ReactNode; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="pointer-events-auto rounded-full px-3 py-1.5 text-xs font-bold text-[#F8F7E5] transition-transform hover:scale-105"
      style={{ background: "rgba(16,23,58,0.75)", border: "1px solid rgba(248,247,229,0.25)" }}>
      {children}
    </button>
  );
}

// ---------- 角色详情 + 聊天 ----------
function AgentPanel({ profile, onClose, onRefresh }: { profile: AgentProfile; onClose: () => void; onRefresh: () => void }) {
  const [tab, setTab] = useState<"chat" | "profile">("chat");
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [memories, setMemories] = useState(profile.memories);
  const [memTotal, setMemTotal] = useState(profile.memoriesTotal ?? profile.memories.length);
  const [loadingMore, setLoadingMore] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    api.chatHistory(profile.id).then((r) => setMsgs(r.messages)).catch(() => {});
  }, [profile.id]);

  // 切换角色时重置记忆列表
  useEffect(() => {
    setMemories(profile.memories);
    setMemTotal(profile.memoriesTotal ?? profile.memories.length);
    setLoadingMore(false);
  }, [profile.id, profile.memories, profile.memoriesTotal]);

  const loadMoreMemories = async () => {
    if (loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.memories(profile.id, memories.length, 100);
      setMemories((prev) => [...prev, ...r.memories]);
      setMemTotal(r.total);
    } catch { /* 网络失败时保留现状,按钮可重试 */ } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [msgs]);

  const send = async () => {
    if (!input.trim() || sending) return;
    const text = input.trim();
    setInput("");
    setSending(true);
    setMsgs((m) => [...m, { id: Date.now(), role: "user", content: text, createdAt: Date.now() }]);
    try {
      const res = await api.chat(profile.id, text);
      setMsgs((m) => [...m, { id: Date.now() + 1, role: "agent", content: res.reply, createdAt: Date.now() }]);
    } catch (e: any) {
      setMsgs((m) => [...m, { id: Date.now() + 1, role: "agent", content: `(${e.message})`, createdAt: Date.now() }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="absolute top-14 right-3 bottom-12 w-96 max-w-[92vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 border-b border-[#ddd5b8] flex items-center gap-2">
        <span className="w-4 h-4 rounded-full" style={{ background: profile.color }} />
        <span className="font-black text-[#1D1D1D]" style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>{profile.name}</span>
        <span className="text-xs text-[#8a8571]">{profile.isNPC ? profile.occupation : "玩家分身"}</span>
        <span className="text-xs text-[#a9a494] ml-1">· {profile.activity}</span>
        <button onClick={onClose} className="ml-auto text-[#8a8571]">✕</button>
      </div>
      <div className="flex border-b border-[#ddd5b8]">
        {(["chat", "profile"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-1 py-2 text-sm font-bold ${tab === t ? "text-[#1D1D1D] border-b-2 border-[#b39c4f]" : "text-[#8a8571]"}`}>
            {t === "chat" ? "💬 聊天" : "📖 档案"}
          </button>
        ))}
      </div>

      {tab === "chat" ? (
        <>
          <div ref={listRef} className="flex-1 overflow-y-auto p-3 space-y-2">
            {msgs.length === 0 && (
              <div className="text-center text-[#a9a494] text-xs py-6">
                和 {profile.name} 打个招呼吧{profile.isNPC ? "" : ",它会记得你说的话"}
              </div>
            )}
            {msgs.map((m) => (
              <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className="max-w-[80%] rounded-2xl px-3 py-2 text-sm leading-relaxed"
                  style={m.role === "user"
                    ? { background: "#e8c96a", color: "#1D1D1D", borderBottomRightRadius: 4 }
                    : { background: "#fff", color: "#4a453a", border: "1px solid #eee8d0", borderBottomLeftRadius: 4 }}>
                  {m.content}
                </div>
              </div>
            ))}
            {sending && <div className="text-xs text-[#a9a494]">{profile.name} 正在想……</div>}
          </div>
          <div className="p-3 border-t border-[#ddd5b8] flex gap-2">
            <input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder={`对 ${profile.name} 说点什么…`} maxLength={200}
              className="flex-1 px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-sm" />
            <button onClick={send} disabled={sending || !input.trim()}
              className="px-4 py-2 rounded-xl font-bold text-sm text-[#1D1D1D] disabled:opacity-50"
              style={{ background: "#e8c96a" }}>
              发送
            </button>
          </div>
        </>
      ) : (
        <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
          <Section title="性格">{profile.personality || "——"}</Section>
          <Section title="心愿">⭐ {profile.wish || "——"}</Section>
          {profile.backstory && <Section title="背景">{profile.backstory}</Section>}
          {(profile.goalLong || profile.goalShort) && (
            <Section title="目标">
              {profile.goalLong && <div>🎯 长期:{profile.goalLong}</div>}
              {profile.goalShort && <div>📌 短期:{profile.goalShort}</div>}
            </Section>
          )}
          <Section title="最近记忆">
            {memories.length === 0 ? <div className="text-[#a9a494]">还没有记忆</div> : (
              <>
                <div className="space-y-1.5">
                  {memories.map((mem, i) => (
                    <div key={i} className="text-xs text-[#6b6252] border-l-2 border-[#ddd5b8] pl-2">
                      <span className="text-[#a9a494]">第{mem.day}天 {fmtMin(mem.gameMin)}</span> · {mem.content}
                    </div>
                  ))}
                </div>
                {memories.length < memTotal && (
                  <button onClick={loadMoreMemories} disabled={loadingMore}
                    className="w-full mt-2 py-1.5 rounded-lg text-xs font-bold text-[#8a8571] border border-[#ddd5b8] hover:bg-[#f3efe0] disabled:opacity-50">
                    {loadingMore ? "加载中…" : `更多记忆(已加载 ${memories.length}/${memTotal})`}
                  </button>
                )}
              </>
            )}
          </Section>
          <button onClick={onRefresh} className="text-xs text-[#8a8571] underline">刷新</button>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs font-black text-[#b39c4f] mb-1">{title}</div>
      <div className="text-[#4a453a] leading-relaxed">{children}</div>
    </div>
  );
}

// ---------- 我的角色管理 ----------
function MyAgentPanel({ agentId, onClose }: { agentId: string; onClose: () => void }) {
  const [profile, setProfile] = useState<AgentProfile | null>(null);
  const [goalLong, setGoalLong] = useState("");
  const [goalShort, setGoalShort] = useState("");
  const [command, setCommand] = useState("");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    api.profile(agentId).then((r) => {
      setProfile(r.profile);
      setGoalLong(r.profile.goalLong || "");
      setGoalShort(r.profile.goalShort || "");
    }).catch(() => {});
  }, [agentId]);

  const save = async (data: any) => {
    try {
      await api.setGoals(data);
      setMsg("✅ 已传达");
      setTimeout(() => setMsg(""), 2000);
    } catch (e: any) {
      setMsg(e.message);
    }
  };

  return (
    <div className="absolute top-14 right-3 bottom-12 w-96 max-w-[92vw] z-10 rounded-2xl overflow-hidden flex flex-col"
      style={{ background: "rgba(248,247,229,0.97)", boxShadow: "0 10px 40px rgba(0,0,0,0.4)" }}>
      <div className="px-4 py-3 border-b border-[#ddd5b8] flex items-center gap-2">
        <span className="font-black text-[#1D1D1D]" style={{ fontFamily: '"ZCOOL KuaiLe","PingFang SC",sans-serif' }}>
          ⭐ {profile?.name ?? "我的角色"}
        </span>
        <span className="text-xs text-[#a9a494]">{profile?.activity}</span>
        <button onClick={onClose} className="ml-auto text-[#8a8571]">✕</button>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-4 text-sm">
        <p className="text-xs text-[#8a8571] leading-relaxed">
          你的分身会自主生活。你可以为它设定目标、下达指令,或点击地面直接带它走动;点击它还能和它心灵对话。
        </p>
        <div>
          <label className="text-xs font-black text-[#b39c4f]">🎯 长期目标</label>
          <textarea value={goalLong} onChange={(e) => setGoalLong(e.target.value)} rows={2} maxLength={100}
            placeholder="例:成为小镇最受欢迎的烘焙师"
            className="mt-1 w-full px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] resize-none text-sm" />
        </div>
        <div>
          <label className="text-xs font-black text-[#b39c4f]">📌 短期目标</label>
          <textarea value={goalShort} onChange={(e) => setGoalShort(e.target.value)} rows={2} maxLength={100}
            placeholder="例:今天去市集认识三个新朋友"
            className="mt-1 w-full px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] resize-none text-sm" />
        </div>
        <button onClick={() => save({ goalLong, goalShort })}
          className="w-full py-2.5 rounded-xl font-bold text-[#1D1D1D]" style={{ background: "#e8c96a" }}>
          保存目标
        </button>
        <div>
          <label className="text-xs font-black text-[#b39c4f]">⚡ 直接指令(立即执行)</label>
          <div className="mt-1 flex gap-2">
            <input value={command} onChange={(e) => setCommand(e.target.value)} maxLength={100}
              placeholder="例:去湖边看日落"
              className="flex-1 px-3 py-2 rounded-xl border border-[#ddd5b8] bg-white outline-none focus:border-[#b39c4f] text-sm" />
            <button onClick={() => { if (command.trim()) { save({ command: command.trim() }); setCommand(""); } }}
              className="px-4 rounded-xl font-bold text-[#F8F7E5]" style={{ background: "#1b2a5e" }}>
              下达
            </button>
          </div>
        </div>
        {msg && <div className="text-xs text-center text-[#6b6252]">{msg}</div>}
      </div>
    </div>
  );
}
